import { useMemo } from 'react';

import type { ConnectionRowAction } from '../utils/connectionRowActionTypes.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import { useAskRovoRowAction } from './useAskRovoRowAction.ts';
import { useSmartLinkPreviewAction } from './useSmartLinkPreviewAction.ts';

/**
 * The full set of actions a connections row offers, derived from the row's own
 * link. Rows own their actions rather than receiving them from the section
 * above: what a link affords depends on the link (is it granted, does it resolve
 * with an embed) and not on which section it happens to sit in.
 *
 * This is the one place to add further derived actions — each contributing hook
 * returns null when its action doesn't apply, and the order here is the order
 * they appear in the row's trailing cluster.
 */
export const useConnectionRowActions = (
	item: EnhancedSmartLink,
	onPreviewOpen?: () => void,
	onAskRovo?: (prompt?: string) => void,
): ConnectionRowAction[] => {
	const previewAction = useSmartLinkPreviewAction(item, onPreviewOpen);
	const askRovoAction = useAskRovoRowAction(item, onAskRovo);

	return useMemo(
		() =>
			[previewAction, askRovoAction].filter(
				(action): action is ConnectionRowAction => action !== null,
			),
		[previewAction, askRovoAction],
	);
};
